<template>
    <div>
        <div v-if="!program.id" class="text-muted text-center py-4">
            Save the program first to configure auto-process products.
        </div>
        <div v-else>
            <!-- Auto Process All Drugs Toggle -->
            <div class="mb-3">
                <checkbox v-model="localAutoprocessAllDrugs" inline @input="onAutoprocessAllDrugsChanged">
                    Auto Process All Drugs
                    <help-popup placement="top">
                        When enabled, all drugs flagged for auto-processing will use the default
                        settings below unless individually overridden in the table.
                    </help-popup>
                </checkbox>
            </div>

            <!-- Default Settings Panel (shown when AutoprocessAllDrugs is on) -->
            <div v-if="localAutoprocessAllDrugs" class="auto-process-defaults mb-3">
                <div class="mb-2"><strong>Default Settings for All Drugs</strong></div>

                <!-- Toggles -->
                <div class="mb-3">
                    <div class="toggle-grid">
                        <checkbox v-model="defaults.passthroughSig">
                            <help-popup placement="top">Use directions from the e-script as-is instead of default directions</help-popup>
                            Passthrough Sig
                        </checkbox>
                        <checkbox v-model="defaults.autoship">
                            <help-popup placement="top">
                                Automatically creates an Order containing the prescription. ⚠ Any patient copay
                                (Prescription.PatientPay) is NOT collected automatically — there is no downstream
                                job that charges the patient's card for an autoship-created Order. Only enable
                                this for prescriptions where the copay is $0 or you have another process for
                                collecting payment.
                            </help-popup>
                            Autoship
                        </checkbox>
                        <checkbox v-model="defaults.autoBillInsurance">
                            <help-popup placement="top">Automatically submit insurance claims during processing</help-popup>
                            Auto Bill Insurance
                        </checkbox>
                        <checkbox v-model="defaults.isConsignment">
                            <help-popup placement="top">Drug is consignment inventory and billed differently</help-popup>
                            Is Consignment
                        </checkbox>
                        <checkbox v-model="defaults.selectBasedOnInventory">
                            <help-popup placement="top">Automatically select therapeutically equivalent drugs based on inventory availability</help-popup>
                            Select Based On Inventory
                        </checkbox>
                        <checkbox v-model="defaults.createPartialPaymentOrder" :disabled="!defaults.autoship">
                            <help-popup placement="top">
                                When Autoship is on and the prescription has a non-zero copay, the auto-created
                                Order starts in Partial Payment status so the patient app surfaces it for online
                                payment and the AutoOutbound text reminder fires. Technicians can also collect the
                                copay from the admin Order page. No effect when Autoship is off or copay is $0.
                            </help-popup>
                            Partial Payment Order
                        </checkbox>
                    </div>
                    <div class="onhold-pair mt-2">
                        <checkbox v-model="defaults.onHold">
                            <help-popup placement="top">Place orders on hold after auto-processing</help-popup>
                            On Hold
                        </checkbox>
                        <OnHoldSubStatusSelect v-model="defaults.onHoldSubStatusId" :disabled="!defaults.onHold"
                                               class="onhold-substatus-select" />
                    </div>
                </div>

                <!-- Value fields -->
                <b-form-row>
                    <b-col md="4">
                        <b-form-group>
                            <template #label>
                                Days Supply Factor
                                <help-popup placement="top">Multiplier applied to the prescribed days supply (e.g. 1.0 = exact, 2.0 = double)</help-popup>
                            </template>
                            <b-input v-model.number="defaults.daysSupplyFactor" type="number" step="0.1" min="0" size="sm" />
                        </b-form-group>
                    </b-col>
                    <b-col md="4">
                        <b-form-group>
                            <template #label>
                                Default Delivery
                                <help-popup placement="top">Delivery method assigned to orders for this drug when auto-processed</help-popup>
                            </template>
                            <b-form-select v-model="defaults.defaultDelivery" size="sm" :options="deliveryCodeOptions" />
                        </b-form-group>
                    </b-col>
                    <b-col md="4">
                        <b-form-group>
                            <template #label>
                                Default Cash Out
                                <help-popup placement="top">Price schedule used to compute the cash price when insurance is not billed. Leave blank to inherit the program's default.</help-popup>
                            </template>
                            <PriceScheduleSelect v-model="defaults.defaultCashOut" />
                        </b-form-group>
                    </b-col>
                </b-form-row>
                <b-form-row>
                    <b-col md="12">
                        <b-form-group>
                            <template #label>
                                Default Directions
                                <help-popup placement="top">Sig directions to use on the prescription. Leave blank to use directions from the e-script</help-popup>
                            </template>
                            <b-input v-model="defaults.defaultDirections" size="sm"
                                placeholder="Leave blank to use e-script directions" />
                        </b-form-group>
                    </b-col>
                </b-form-row>
                <b-form-row>
                    <b-col md="12">
                        <b-form-group>
                            <template #label>
                                Webhook URL
                                <help-popup placement="top">URL to receive a POST notification when this drug is auto-processed</help-popup>
                            </template>
                            <b-input v-model="defaults.webhookURL" size="sm" placeholder="https://" />
                        </b-form-group>
                    </b-col>
                </b-form-row>
                <p class="text-muted mb-0">
                    <small>Changes are saved with the program's <strong>Save Changes</strong> button.</small>
                </p>
            </div>

            <hr />

            <!-- Drug Overrides Section -->
            <div class="d-flex justify-content-between align-items-center mb-3">
                <strong>Drug Overrides</strong>
                <div>
                    <history-modal ref="historyModal" v-model="program" hide-button
                        search-type-override="Auto Process Product" />
                    <b-button variant="outline-info" size="sm" class="mr-2" @click="openHistoryModal">
                        <i class="fas fa-history mr-1"></i> History
                    </b-button>
                    <b-button variant="primary" size="sm" @click="openAddModal">
                        <i class="fas fa-plus mr-1"></i> Add Drug
                    </b-button>
                </div>
            </div>

            <!-- Search -->
            <b-form-row class="mb-3">
                <b-col md="6">
                    <b-input v-model="searchTerm" placeholder="Search by drug name or NDC..." size="sm"
                        debounce="400" @update="resetAndLoad" />
                </b-col>
            </b-form-row>

            <!-- Table -->
            <b-table ref="table" :items="products" :fields="fields" :busy="loading" show-empty
                :sort-by.sync="sortBy" :sort-desc.sync="sortDesc" :no-local-sorting="true"
                @sort-changed="onSortChanged" selectable select-mode="multi"
                @row-selected="onRowSelected" small hover responsive>

                <template #head(selected)>
                    <b-form-checkbox :checked="allSelected" :indeterminate="someSelected"
                        @change="toggleSelectAll" />
                </template>

                <template #cell(selected)="{ rowSelected }">
                    <i :class="rowSelected ? 'fas fa-check-square text-primary' : 'far fa-square text-muted'" />
                </template>

                <template #cell(drugName)="{ item }">
                    {{ item.drugName || 'Unknown Drug' }}
                </template>

                <template #cell(ndc11)="{ item }">
                    <code>{{ formatNdc(item.ndc11) }}</code>
                </template>

                <template #cell(autoship)="{ item }">
                    <i :class="item.autoship ? 'fas fa-check text-success' : 'fas fa-times text-muted'" />
                </template>

                <template #cell(onHold)="{ item }">
                    <i :class="item.onHold ? 'fas fa-check text-warning' : 'fas fa-times text-muted'" />
                </template>

                <template #cell(passthroughSig)="{ item }">
                    <i :class="item.passthroughSig ? 'fas fa-check text-success' : 'fas fa-times text-muted'" />
                </template>

                <template #cell(autoBillInsurance)="{ item }">
                    <i :class="item.autoBillInsurance ? 'fas fa-check text-success' : 'fas fa-times text-muted'" />
                </template>

                <template #cell(selectBasedOnInventory)="{ item }">
                    <i :class="item.selectBasedOnInventory ? 'fas fa-check text-success' : 'fas fa-times text-muted'" />
                </template>

                <template #cell(daysSupplyFactor)="{ item }">
                    {{ item.daysSupplyFactor }}
                </template>

                <template #cell(updated)="{ item }">
                    <small>{{ item.updated ? new Date(item.updated).toLocaleDateString() : '' }}</small>
                </template>

                <template #cell(actions)="{ item }">
                    <b-button variant="link" size="sm" class="row-action mr-1" title="Edit" @click.stop="openEditModal(item)">
                        <i class="fas fa-edit" />
                    </b-button>
                    <b-button variant="link" size="sm" class="row-action text-danger" title="Delete"
                        @click.stop="confirmDelete(item)">
                        <i class="fas fa-trash" />
                    </b-button>
                </template>

                <template #empty>
                    <div class="text-center text-muted py-3">
                        No auto-process products configured for this program.
                    </div>
                </template>

                <template #table-busy>
                    <div class="text-center py-3">
                        <b-spinner small label="Loading..." />
                    </div>
                </template>
            </b-table>

            <!-- Pager -->
            <BottomPagerBar v-model="pageNumber" :total-rows="totalRows" :per-page="pageSize"
                :loading="loading" :selected-rows="selectedProducts.length" show-total show-page-size-selector
                item-name="product" scroll-target=".program-component"
                @input="load" @change-page-size="onPageSizeChanged" @clear-selection="clearSelection">
                <template v-if="selectedProducts.length > 0" #fab>
                    <b-button size="sm" variant="primary" @click="openBulkEditModal">
                        <i class="fas fa-edit mr-1" /> Bulk Edit
                    </b-button>
                    <b-button size="sm" variant="danger" @click="confirmBulkDelete">
                        <i class="fas fa-trash mr-1" /> Delete
                    </b-button>
                </template>
            </BottomPagerBar>

            <!-- Edit Modal -->
            <AutoProcessProductEditModal ref="editModal" :program-id="program.id"
                @saved="onProductSaved" />

            <!-- Bulk Edit Modal -->
            <AutoProcessBulkEditModal ref="bulkEditModal" :program-id="program.id"
                @saved="onBulkEditSaved" />

        </div>
    </div>
</template>

<script lang="ts">
import axios, { CancelTokenSource } from 'axios';
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { AutoProcessProduct, AutoProcessProductPage, AutoProcessDefaults, createDefaultAutoProcessDefaults } from '@/models/AutoProcessProduct';
import { NotificationOptions } from '@/util/NotificationOptionsPresets';
import { getPageSizePreference, savePageSizePreference } from '@/util/pageSizePreference';
import BottomPagerBar from '@/components/BottomPagerBar.vue';
import Checkbox from '@/components/Inputs/Checkbox.vue';
import HelpPopup from '@/components/HelpPopup.vue';
import { DeliveryCode } from '@/models/DeliveryCode';
import AutoProcessProductEditModal from '@/components/Program/AutoProcessProductEditModal.vue';
import AutoProcessBulkEditModal from '@/components/Program/AutoProcessBulkEditModal.vue';
import OnHoldSubStatusSelect from '@/components/Program/OnHoldSubStatusSelect.vue';
import PriceScheduleSelect from '@/components/Program/PriceScheduleSelect.vue';
import HistoryModal from '@/components/Modals/HistoryModal.vue';
import { Program } from '@/models/Program';
import { enumToOptions } from '@/util/EnumOptions';
import TextUtils from '@/util/TextUtils';

@Component({
    name: 'ProgramAutoProcessTab',
    components: {
        BottomPagerBar,
        Checkbox,
        HelpPopup,
        AutoProcessProductEditModal,
        AutoProcessBulkEditModal,
        OnHoldSubStatusSelect,
        PriceScheduleSelect,
        HistoryModal,
    },
})
export default class ProgramAutoProcessTab extends Vue {
    @Prop({ required: true }) program!: Program;

    private products: AutoProcessProduct[] = [];
    private totalRows = 0;
    private pageNumber = 1;
    private pageSize = 100;
    private searchTerm = '';
    private sortBy = 'drugName';
    private sortDesc = false;
    private loading = false;
    private selectedProducts: AutoProcessProduct[] = [];
    private cancelTokenSource: CancelTokenSource | null = null;
    private localAutoprocessAllDrugs = false;
    private defaults: AutoProcessDefaults = createDefaultAutoProcessDefaults();

    // Built once at class-init time from the DeliveryCode enum mirror. Numeric
    // values pair with the worker's (int)Prescription.DeliveryCodeEnum cast in
    // CreateOrderForAutoShip.
    private deliveryCodeOptions = enumToOptions(DeliveryCode, { includeNone: true });

    private fields = [
        { key: 'selected', label: '', sortable: false, class: 'col-checkbox' },
        { key: 'drugName', label: 'Drug Name', sortable: true },
        { key: 'ndc11', label: 'NDC', sortable: true },
        { key: 'packageSize', label: 'Pkg Size', sortable: false },
        { key: 'autoship', label: 'Autoship', sortable: true, class: 'text-center' },
        { key: 'onHold', label: 'Hold', sortable: true, class: 'text-center' },
        { key: 'passthroughSig', label: 'Pass Sig', sortable: true, class: 'text-center' },
        { key: 'autoBillInsurance', label: 'Auto Bill', sortable: true, class: 'text-center' },
        { key: 'selectBasedOnInventory', label: 'Inv Select', sortable: true, class: 'text-center' },
        { key: 'daysSupplyFactor', label: 'DS Factor', sortable: true },
        { key: 'updatedBy', label: 'Updated By', sortable: true },
        { key: 'updated', label: 'Updated', sortable: true },
        { key: 'actions', label: '', sortable: false, class: 'text-right' },
    ];

    async created() {
        this.pageSize = getPageSizePreference(this, 'pageSize_AutoProcessProducts', 100);
        this.syncFromProgram();
    }

    mounted() {
        if (this.program.id) {
            this.load();
        }
    }

    @Watch('program.id')
    onProgramChanged() {
        // Parent loads the program asynchronously — re-init local state from the
        // freshly-populated program prop, otherwise the defaults panel renders the
        // empty createDefaultAutoProcessDefaults() values that were set during
        // created() before the program prop was populated.
        this.syncFromProgram();
        if (this.program.id) {
            this.resetAndLoad();
        }
    }

    @Watch('program.autoprocessDefaults')
    onAutoprocessDefaultsPropChanged() {
        this.syncFromProgram();
    }

    // Single source of truth for projecting the parent's program prop into the tab's
    // local state. Called from created() AND from the program.id / autoprocessDefaults
    // watchers so async loads + tab-switches don't strand the defaults panel on its
    // initial-empty values.
    private syncingFromProgram = false;
    private syncFromProgram() {
        this.syncingFromProgram = true;
        try {
            this.localAutoprocessAllDrugs = this.program.autoprocessAllDrugs ?? false;

            const raw = this.program.autoprocessDefaults;
            if (raw) {
                try {
                    this.defaults = {
                        ...createDefaultAutoProcessDefaults(),
                        ...(typeof raw === 'string' ? JSON.parse(raw) : raw),
                    };
                } catch {
                    this.defaults = createDefaultAutoProcessDefaults();
                }
            } else {
                this.defaults = createDefaultAutoProcessDefaults();
            }
        } finally {
            // Defer clearing the guard so the deep-watch on `defaults` (which fires
            // asynchronously) sees the flag and skips the echo-back emit.
            this.$nextTick(() => { this.syncingFromProgram = false; });
        }
    }

    get allSelected(): boolean {
        return this.products.length > 0 && this.selectedProducts.length === this.products.length;
    }

    get someSelected(): boolean {
        return this.selectedProducts.length > 0 && !this.allSelected;
    }

    formatNdc(ndc: string | null): string {
        return TextUtils.formatNdc11(ndc);
    }

    async load() {
        if (!this.program.id) return;

        this.cancelTokenSource?.cancel();
        this.cancelTokenSource = axios.CancelToken.source();

        this.loading = true;
        try {
            const params = {
                pageNumber: this.pageNumber,
                pageSize: this.pageSize,
                searchTerm: this.searchTerm || undefined,
                sortBy: this.sortBy || undefined,
                sortDirection: this.sortDesc ? 'DESC' : 'ASC',
            };

            const { data } = await axios.get<AutoProcessProductPage>(
                `/api/program/${this.program.id}/auto-process-products`,
                { params, cancelToken: this.cancelTokenSource.token }
            );

            this.products = data.items;
            this.totalRows = data.totalRows;
        } catch (e: any) {
            if (!axios.isCancel(e)) {
                (this as any).$notifications?.notify(NotificationOptions.error(e));
            }
        } finally {
            this.loading = false;
        }
    }

    resetAndLoad() {
        this.pageNumber = 1;
        this.clearSelection();
        this.load();
    }

    onSortChanged(ctx: { sortBy: string; sortDesc: boolean }) {
        this.sortBy = ctx.sortBy;
        this.sortDesc = ctx.sortDesc;
        this.resetAndLoad();
    }

    onPageSizeChanged(size: number) {
        this.pageSize = size;
        savePageSizePreference(this, 'pageSize_AutoProcessProducts', size);
        this.resetAndLoad();
    }

    onRowSelected(items: AutoProcessProduct[]) {
        this.selectedProducts = items;
    }

    toggleSelectAll(checked: boolean) {
        const table = this.$refs.table as any;
        if (checked) {
            table.selectAllRows();
        } else {
            table.clearSelected();
        }
    }

    clearSelection() {
        this.selectedProducts = [];
        (this.$refs.table as any)?.clearSelected?.();
    }

    // --- CRUD ---

    openHistoryModal() {
        (this.$refs.historyModal as any).showHistoryModal = true;
    }

    openAddModal() {
        (this.$refs.editModal as any).open(null);
    }

    openEditModal(product: AutoProcessProduct) {
        (this.$refs.editModal as any).open(product);
    }

    onProductSaved() {
        this.load();
    }

    openBulkEditModal() {
        (this.$refs.bulkEditModal as any).open(this.selectedProducts);
    }

    onBulkEditSaved() {
        this.clearSelection();
        this.load();
    }

    async confirmDelete(product: AutoProcessProduct) {
        const confirmed = await (this as any).$bvModal.msgBoxConfirm(
            `Remove "${product.drugName || product.ndc11}" from auto-process?`,
            { title: 'Confirm Delete', okVariant: 'danger', okTitle: 'Delete' }
        );
        if (!confirmed) return;

        try {
            await axios.delete(`/api/program/${this.program.id}/auto-process-products/${product.id}`);
            (this as any).$notifications?.notify(NotificationOptions.success('Product removed.'));
            this.load();
        } catch (e: any) {
            (this as any).$notifications?.notify(NotificationOptions.error(e));
        }
    }

    async confirmBulkDelete() {
        const count = this.selectedProducts.length;
        const confirmed = await (this as any).$bvModal.msgBoxConfirm(
            `Remove ${count} product${count !== 1 ? 's' : ''} from auto-process?`,
            { title: 'Confirm Bulk Delete', okVariant: 'danger', okTitle: 'Delete' }
        );
        if (!confirmed) return;

        try {
            const ids = this.selectedProducts.map(p => p.id);
            await axios.post(`/api/program/${this.program.id}/auto-process-products/bulk-delete`, ids);
            (this as any).$notifications?.notify(NotificationOptions.success(`${count} product(s) removed.`));
            this.clearSelection();
            this.load();
        } catch (e: any) {
            (this as any).$notifications?.notify(NotificationOptions.error(e));
        }
    }

    // --- AutoprocessAllDrugs toggle ---

    onAutoprocessAllDrugsChanged(val: boolean) {
        // Buffer the change onto the parent's program object. The main program "Save
        // Changes" button persists everything (program fields + autoprocess defaults)
        // through the standard POST /api/program/ path.
        this.$emit('update:autoprocessAllDrugs', val);
    }

    // The defaults object is locally reactive. Whenever any field changes, propagate
    // the serialized JSON to the parent so it's included in the next program save.
    @Watch('defaults', { deep: true })
    onDefaultsChanged() {
        // Skip when the change came from syncFromProgram() — otherwise we'd
        // emit-back what the parent just gave us and confuse the dirty-tracking on
        // the parent's save button.
        if (this.syncingFromProgram) return;
        this.$emit('update:autoprocessDefaults', JSON.stringify(this.defaults));
    }
}
</script>

<style scoped lang="scss">
.col-checkbox {
    width: 40px;
    max-width: 40px;
}

// Row edit/delete buttons — `b-button size="sm"` ships at 12px which gets lost on a
// dense table. Bumping to 16px with a clickable touch-target.
.row-action {
    padding: 0.25rem 0.5rem;
    font-size: 16px;
    line-height: 1;
}

.row-action:hover {
    background-color: rgba(0, 0, 0, 0.05);
    border-radius: 0.25rem;
}

.auto-process-defaults {
    border: 1px solid #dee2e6;
    border-radius: 0.25rem;
    padding: 1rem;
    background: transparent;
}

.toggle-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 8px;
}

.toggle-grid label {
    white-space: nowrap;
}

.onhold-pair {
    display: flex;
    align-items: center;
    gap: 12px;
}

.onhold-pair ::v-deep .form-check {
    margin: 0;
    min-height: 0;
    display: flex;
    align-items: center;
}

.onhold-pair > .onhold-substatus-select {
    flex: 0 0 280px;
    max-width: 280px;
}


// Backstop for column alignment: if a long label + help-popup ever wraps to
// two lines again, align the b-cols at the bottom so the input controls stay
// on the same baseline across the row.
.auto-process-defaults ::v-deep .form-row {
    align-items: flex-end;
}

.auto-process-defaults ::v-deep .form-row .form-group {
    margin-bottom: 0;
    width: 100%;
}

::v-deep .floating-action-bar {
    background: rgba(255, 255, 255, 0.95);
    backdrop-filter: blur(10px);
    color: #333;
    border: 1px solid #dee2e6;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);

    .btn-link {
        color: #007bff;
    }
}
</style>
